import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';
import { Edit } from '@swyftx/aviary/icons/outlined';
import { MarginPosition, ProtectionOrderVariant } from '@swyftx/margin/build/types';
import { UpdateMarginPosition } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstrumentDetails, useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginPositionDetails, useUpdateMarginPosition } from 'src/lib/margin/hooks/positions';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';

type Props = {
  size: 'small' | 'large';
  position?: MarginPosition;
  protectionOrderType: ProtectionOrderVariant;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onRemove?: React.MouseEventHandler<HTMLButtonElement>;
};

const MarginPositionDetailsProtectionOrder: React.FC<Props> = ({
  size,
  position,
  protectionOrderType,
  onClick,
  onRemove,
}) => {
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { addSnackBar } = useSnackBars();
  const { updatePosition, invalidateCache } = useUpdateMarginPosition();
  const { isStalePriceData } = useMarginPrices();
  const { getMarketByPosition } = useMarginInstruments();
  const { account } = useMarginAccount();
  const { symbol } = useMarginInstrumentDetails({ instrument: getMarketByPosition(position) });
  const { t } = useTranslation('margin');
  const { stopLoss, takeProfit, isShort } = useMarginPositionDetails({ positionId: position?.positionId });

  const value = useMemo(() => {
    if (protectionOrderType === ProtectionOrderVariant.StopLoss) return stopLoss;
    return takeProfit;
  }, [protectionOrderType, stopLoss, takeProfit]);

  const onUpdatePosition = useCallback(async () => {
    if (!position) return;

    try {
      setLoading(true);

      const positionUpdate: UpdateMarginPosition = {
        takeProfit: protectionOrderType === ProtectionOrderVariant.TakeProfit ? '0' : undefined,
        stopLoss: protectionOrderType === ProtectionOrderVariant.StopLoss ? '0' : undefined,
      };

      await updatePosition(position.positionId, positionUpdate);

      addSnackBar({
        variant: 'success',
        content: isShort
          ? t('protectionOrder.shortPositionUpdated', { symbol })
          : t('protectionOrder.longPositionUpdated', { symbol }),
        showIcon: true,
        duration: 5_000,
      });
    } catch (e) {
      addSnackBar({
        variant: 'error',
        content: t('protectionOrder.positionFailedUpdate', { symbol }),
        showIcon: true,
        label: 'View order',
        duration: 5_000,
      });

      invalidateCache();
    } finally {
      setLoading(false);
      setRemoveModalOpen(false);
    }
  }, [addSnackBar, invalidateCache, isShort, position, protectionOrderType, symbol, t, updatePosition]);

  const disabled = useMemo(() => !account?.active || isStalePriceData(), [account?.active, isStalePriceData]);

  const stalePriceAddTooltip = useMemo(
    () =>
      isStalePriceData()
        ? protectionOrderType === ProtectionOrderVariant.StopLoss
          ? t('stalePrice.tooltips.addStopLoss')
          : t('stalePrice.tooltips.addTakeProfit')
        : undefined,
    [isStalePriceData, protectionOrderType, t],
  );

  const stalePriceEditTooltip = useMemo(
    () =>
      isStalePriceData()
        ? protectionOrderType === ProtectionOrderVariant.StopLoss
          ? t('stalePrice.tooltips.editStopLoss')
          : t('stalePrice.tooltips.editTakeProfit')
        : undefined,
    [isStalePriceData, protectionOrderType, t],
  );

  if (size === 'small') {
    if (value && Big(value).gt(0)) {
      return (
        <FlexLayout justifyContent='end' alignItems='center' spacing={4}>
          <Numeric>{formatCurrency(value)}</Numeric>
          <Button
            variant='outlined'
            size='sm'
            tooltip={stalePriceEditTooltip}
            layout='icon'
            leadingIcon={<Edit />}
            disabled={disabled}
            onClick={onClick}
          />
        </FlexLayout>
      );
    }
    return (
      <FlexLayout justifyContent='end' alignItems='center'>
        <Button variant='outlined' size='sm' onClick={onClick} disabled={disabled} tooltip={stalePriceAddTooltip}>
          {t('protectionOrder.buttons.add')}
        </Button>
      </FlexLayout>
    );
  }

  if (value && Big(value).gt(0)) {
    return (
      <FlexLayout justifyContent='end' alignItems='center' spacing={8}>
        <Modal
          overlayClassName='z-modal-popover-backdrop'
          title={t('protectionOrder.title')}
          position='bottom'
          open={removeModalOpen}
          onOpenChange={(o) => setRemoveModalOpen(!!o)}
          triggerElement={
            <Button variant='outlined' size='sm' className='block sm:hidden' disabled={!account?.active}>
              {t('protectionOrder.buttons.remove')}
            </Button>
          }
        >
          <FlexLayout direction='column' className='px-16 pb-16' spacing={16}>
            <Body color='secondary'>{t('protectionOrder.content')}</Body>
            <FlexLayout alignItems='center' spacing={16}>
              <Button
                variant='outlined'
                onClick={() => setRemoveModalOpen(false)}
                className='w-full'
                size='lg'
                disabled={!account?.active}
              >
                {t('protectionOrder.buttons.cancel')}
              </Button>
              <Button
                variant='filled'
                color='destructive'
                onClick={onUpdatePosition}
                className='w-full'
                size='lg'
                disabled={!account?.active}
                loading={loading}
              >
                {t('protectionOrder.buttons.yesRemove')}
              </Button>
            </FlexLayout>
          </FlexLayout>
        </Modal>

        <Button variant='outlined' size='sm' className='hidden sm:block' onClick={onRemove} disabled={!account?.active}>
          {t('protectionOrder.buttons.remove')}
        </Button>
        <Button variant='outlined' size='sm' onClick={onClick} disabled={disabled} tooltip={stalePriceEditTooltip}>
          {t('protectionOrder.buttons.edit')}
        </Button>
        <Numeric>{formatCurrency(value)}</Numeric>
      </FlexLayout>
    );
  }

  return (
    <FlexLayout justifyContent='end' alignItems='center'>
      <Button variant='outlined' size='sm' onClick={onClick} disabled={!account?.active}>
        {t('protectionOrder.buttons.addOrder')}
      </Button>
    </FlexLayout>
  );
};

export { MarginPositionDetailsProtectionOrder };
